import { useState, useEffect, useCallback, type RefObject } from 'react'

interface UseBackgroundContrastOptions {
  containerRef?: RefObject<HTMLElement | null>
}

/** Parse an rgb/rgba string and return [r, g, b, a] */
function parseColor(color: string): [number, number, number, number] | null {
  const match = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/)
  if (!match) return null
  return [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    match[4] !== undefined ? Number(match[4]) : 1,
  ]
}

/** Compute perceived luminance (0 = black, 1 = white) */
function luminance(r: number, g: number, b: number): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

/**
 * Walk up the DOM tree from `startEl` to find the first ancestor
 * with a non-transparent background color and return its luminance.
 * Falls back to 1 (white/light) if nothing is found.
 */
function resolveBackgroundLuminance(startEl: Element): number {
  let el: Element | null = startEl
  while (el && el !== document.documentElement) {
    const bg = getComputedStyle(el).backgroundColor
    const parsed = parseColor(bg)
    if (parsed) {
      const [r, g, b, a] = parsed
      // Skip fully transparent backgrounds
      if (a > 0.1) {
        return luminance(r, g, b)
      }
    }
    el = el.parentElement
  }
  // Nothing found — assume light (white page default)
  return 1
}

/**
 * Detects whether the background behind a given element is dark or light.
 * Walks up the DOM from the element at the center point until a
 * non-transparent background is found.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
  options: UseBackgroundContrastOptions = {}
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  const check = useCallback(() => {
    const element = elementRef.current
    if (!element) return

    const rect = element.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top + rect.height / 2

    // Hide the entire container (not just the button) so elementFromPoint
    // can see through to the actual page content behind it
    const container = options.containerRef?.current
    const hideTarget = container || element
    const prev = hideTarget.style.visibility
    hideTarget.style.visibility = 'hidden'
    const behind = document.elementFromPoint(x, y)
    hideTarget.style.visibility = prev

    if (!behind) return

    const lum = resolveBackgroundLuminance(behind)
    setBackground(lum < 0.45 ? 'dark' : 'light')
  }, [elementRef, options.containerRef])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    check()

    const observer = new IntersectionObserver(check, {
      root: options.containerRef?.current ?? null,
      threshold: [0, 0.5, 1],
    })
    observer.observe(element)

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [elementRef, options.containerRef, check])

  return background
}
